import './index.css'
import * as React from 'react'
import HelpTooltip from 'components/common/tooltip'
export type LabelProps = {
  caption: React.ReactNode
  validationError?: string | null | undefined
  required?: boolean
  tooltipContent?: React.ReactNode
  tooltipPosition?: 'top' | 'bottom'
  tooltipId?: string
  children?: React.ReactNode
  hidden?: boolean
}

/**
 * @deprecated This component should be ported to use design system components underneath.
 * @see {@link components/ds/Text}
 * @see {@link components/ds/Flex}
 * @see {@link components/ds/Tooltip}
 * @migration
 * - This component provides useful form label functionality (required indicators, validation errors, help tooltips)
 * - Instead of removing it, port this component to use DS primitives internally:
 *   - Use `<Flex direction="column" gap="1">` for layout
 *   - Use `<Text as="label">` for caption
 *   - Use `<Text color="red">*</Text>` for required indicator
 *   - Use `<Text size="1" color="red">` for validation errors
 *   - Use DS `<Tooltip>` for help tooltips
 * - Keep the existing API surface (caption, required, validationError, tooltipContent props)
 * - Create as a new component in `components/ds/FormField` or `components/ds/Label`
 * - Once ported, update imports throughout the codebase to use the new DS-based version
 *
 * A form label component that supports required indicators, validation errors, and help tooltips.
 * Provides accessible labeling for form inputs with optional help text and error messaging.
 *
 * @param caption - The label text or content to display
 * @param validationError - Optional validation error message to display below the label
 * @param required - Whether the field is required (shows asterisk indicator)
 * @param tooltipContent - Optional content to display in the help tooltip
 * @param tooltipPosition - Position of the tooltip relative to the help icon (default: 'top')
 * @param tooltipId - Optional tooltip ID for accessibility
 * @param children - Optional child elements to render below the label
 * @param hidden - Whether to visually hide the label caption while keeping it accessible
 * @returns The label component with optional tooltip, error message, and children
 */
const Label = ({
  caption,
  required,
  validationError,
  tooltipContent,
  tooltipPosition,
  tooltipId,
  children,
  hidden,
}: LabelProps): React.ReactElement<'div'> => (
  <div className="label">
    <label className="Form_Label">
      <span className={hidden ? 'label__caption--hidden' : undefined}>
        {caption}
        {required && (
          <span className="label__caption__required" title="Field is required">
            *
          </span>
        )}
      </span>
      {(tooltipContent || tooltipId) && (
        <HelpTooltip id={tooltipId} position={tooltipPosition}>
          {tooltipContent}
        </HelpTooltip>
      )}
    </label>
    {validationError && <div className="label__error">{validationError}</div>}
    {children}
  </div>
)

export default Label
